import { addHours } from 'date-fns/addHours';
import { isAfter } from 'date-fns/isAfter';
import { startOfMinute } from 'date-fns/startOfMinute';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useIframeDialogPositioning } from 'src/hooks/useIframeDialogPositioning';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTimePicker } from 'src/shared/Forms/Fields/FormTimePicker';
import { timestampToDate } from 'src/utils/dateFormatter';
import * as Yup from 'yup';

import type { Timestamp } from 'src/types/utilityTypes';

interface ScheduleMessageDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (scheduledTime: Date) => void;
  title?: string;
  submitLabel?: string;
  initialTime?: Timestamp | Date | null;
}

const ScheduleSchema = Yup.object().shape({
  scheduledTime: Yup.date()
    .nullable()
    .required('Please select a date and time')
    .test('is-future', 'Scheduled time must be in the future', (value) => {
      if (!value) return false;
      const now = startOfMinute(new Date());
      return isAfter(value, now);
    }),
});

export const ScheduleMessageDialog: React.FC<ScheduleMessageDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title = 'Schedule Message',
  submitLabel = 'Schedule Message',
  initialTime = null,
}) => {
  // Use iframe dialog positioning hook
  const { dialogSx } = useIframeDialogPositioning();

  const handleSubmit = (
    values: { scheduledTime: Date | null },
    { setSubmitting }: any
  ) => {
    setSubmitting(false);
    if (values.scheduledTime) {
      onSubmit(values.scheduledTime);
    }
    onClose();
  };

  const initialDate = initialTime
    ? timestampToDate(initialTime)
    : addHours(new Date(), 1);

  return (
    <Formik
      initialValues={{ scheduledTime: initialDate }}
      enableReinitialize
      validationSchema={ScheduleSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps: FormikProps<{ scheduledTime: Date }>) => (
        <Dialog
          title={title}
          open={open}
          onClose={onClose}
          sx={dialogSx}
          actions={[
            {
              variant: 'outlined',
              label: 'Cancel',
              onClick: onClose,
            },
            {
              variant: 'contained',
              label: submitLabel,
              onClick: formikProps.submitForm,
              disabled: !formikProps.isValid || formikProps.isSubmitting,
            },
          ]}
        >
          <Form>
            <Stack direction="row" spacing={2}>
              <FormDatePicker name="scheduledTime" label="Select Date" />
              <FormTimePicker name="scheduledTime" label="Select Time" />
            </Stack>
            {formikProps.errors.scheduledTime && (
              <Typography color="error" variant="caption">
                {formikProps.errors.scheduledTime as string}
              </Typography>
            )}
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};
